import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CldImage } from "next-cloudinary";
import Pages from "components/ui/Pages/Pages";
import FormInput from "components/guide/create/form/FormInput";
const GuideLoginPage = dynamic(() => import("./pages/GuideLoginPage"));
const ConfirmationPage = dynamic(() => import("./pages/ConfirmationPage"));
import {
  GuideFormPageData,
  images,
} from "components/guide/create/form/data/create-guide";
import { createGuideSchema } from "components/guide/create/form/data/guide-schemas";

import styles from "./FormContainer.module.scss";

export default function FormContainer({}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1); // currentPageNumber doesn't affect actual form; simply there to keep in sync with background images
  const [responseData, setResponseData] = useState({});
  const [generationStatus, setGenerationStatus] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);

  const router = useRouter();
  const { asPath, query } = router;

  const portalRef = useRef(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    control,
    formState,
    trigger,
    setError,
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(createGuideSchema),
    mode: "all",
    reValidateMode: "all",
  });

  const formValues = watch();

  let { errors } = formState;

  const pages = GuideFormPageData();

  useEffect(() => {
    if (!router.isReady || isLoaded) {
      return;
    }

    setIsLoaded(true);
  }, [router.isReady]);

  useEffect(() => {
    // If user has navigated to a page with a task Id in query, direct to loading page

    const { functionId, queueId, functionStatusUrl } = query;

    if (!functionId && !queueId && !functionStatusUrl) {
      return;
    }

    if (functionStatusUrl || queueId) {
      setIsSubmitting(true);
    }
  }, [
    query?.functionId,
    router?.query,
    setIsSubmitting,
    query?.queueId,
    query?.functionStatusUrl,
    query,
  ]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    // Handle malformed Clerk redirect response on registration
    // const isMalformed =
    //   asPath.includes("#/verify?") || asPath.includes("#/sso-callback?");
    // let newUrl = isMalformed
    //   ? asPath.replace("#/verify?", "&").replace("#/sso-callback?", "&")
    //   : asPath;
    const isMalformed = asPath.includes("#/verify?");
    let newUrl = isMalformed ? asPath.replace("#/verify?", "&") : asPath;

    const newUrlQueryParams = new URLSearchParams(newUrl);
    const redirectUrl =
      query?.redirect_url ?? newUrlQueryParams.get("redirect_url");

    if (!!redirectUrl) {
      console.log("Redirecting... ", redirectUrl);
      router.replace(redirectUrl);
      return;
    }

    if (isMalformed && newUrl) {
      console.log("Redirecting... ", newUrl);
      router.replace(newUrl);
      return;
    }

    return;
  }, [asPath, query?.redirect_url, router, router.query]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    // Check if form values are passed in query params
    const formValuesParamComponent = router?.query?.travel_guide_form_values;

    if (
      !formValuesParamComponent &&
      router.isReady &&
      !window.location.toString().includes("travel_guide_form_values")
    ) {
      return;
    }

    function populateFormValuesFromQueryParam({ queryFormValues }) {
      try {
        if (
          !queryFormValues &&
          !window.location.toString().includes("travel_guide_form_values")
        ) {
          console.log("No form values in query params");
          return false;
        }

        const formValuesParamString = decodeURIComponent(queryFormValues);

        let sessionFormState = formValuesParamString;

        if (
          !sessionFormState ||
          sessionFormState == undefined ||
          sessionFormState == "undefined"
        ) {
          return false;
        }
        // console.log("undefined? ", sessionFormState == undefined);
        // console.log("type: ", typeof sessionFormState);

        if (
          sessionFormState &&
          sessionFormState !== "null" &&
          sessionFormState !== "undefined"
        ) {
          let initialFormState = JSON.parse(sessionFormState);

          for (let formEntry of Object.entries(initialFormState)) {
            const [inputId, value] = formEntry;
            setValue(inputId, value); //
          }
        }

        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    const populationSuccess = populateFormValuesFromQueryParam({
      queryFormValues: formValuesParamComponent,
    });

    // Clear query params after initial load so that they don't continue to override session storage
    const currentFormValues = getValues();

    function clearFormValuesSearchParam() {
      const newParams = new URLSearchParams(router.query);
      newParams.delete("travel_guide_form_values");

      router.replace(
        {
          pathname: router.pathname,
          query: newParams.toString(),
        },
        null,
        { shallow: true }
      );
    }

    const formValuesString = JSON.stringify(currentFormValues);
    const formComponentString = decodeURIComponent(formValuesParamComponent);

    if (populationSuccess && formValuesString === formComponentString) {
      clearFormValuesSearchParam();
    } else {
      console.error(
        "Query param detected, but failed to populate form; falling back to URL"
      );

      const windowUrl = new URL(window.location);

      const searchParams = new URLSearchParams(windowUrl.search);
      const windowUrlFormValues = searchParams.get("travel_guide_form_values");
      const populationSuccessRetry = populateFormValuesFromQueryParam({
        queryFormValues: windowUrlFormValues,
      });

      if (populationSuccessRetry) {
        console.log("clearing...");
        clearFormValuesSearchParam();
      }
    }

    // Trigger on load to ensure that Next button is disabled if location not set
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    router,
    router.asPath,
    setValue,
    router.query,
    query.travel_guide_form_values,
    router.isReady,
    isLoaded,
    trigger,
  ]);

  // useEffect(() => {
  //   if (!isLoaded) {
  //     return;
  //   }
  //   const formValuesParamComponent = router?.query?.travel_guide_form_values;

  //   if (!!formValuesParamComponent || !!router?.query?.clear) {
  //     return;
  //   }

  //   // Check if formState exists in session storage
  //   const localStorageString = localStorage.getItem("travel_guide_form_values");

  //   // URL params take precedence over session storage
  //   let sessionFormState = localStorageString;

  //   if (sessionFormState) {
  //     let initialFormState = JSON.parse(sessionFormState);

  //     for (let formEntry of Object.entries(initialFormState)) {
  //       const [inputId, value] = formEntry;
  //       setValue(inputId, value); //
  //     }
  //   }

  //   // Trigger on load to ensure that Next button is disabled if location not set
  //   trigger ? trigger() : "";
  // }, [isLoaded]);

  useEffect(() => {
    if (!isLoaded || !router.isReady) {
      return;
    }
    // Check if "clear" parameter is present in query params
    const clearParam = query?.clear;

    if (!clearParam || !router?.query?.page) {
      return;
    }

    // Clear query params after initial load so that they don't continue to override session storage
    const newParams = new URLSearchParams(router.query);
    newParams.delete("clear");

    router.replace(
      {
        pathname: router.pathname,
        query: newParams.toString(),
      },
      null,
      { shallow: true }
    );

    // Clear form values
    reset();
    clearErrors();

    // Clear session storage
    localStorage.removeItem("travel_guide_form_values");
    sessionStorage.removeItem("travel_guide_form_errors");
  }, [router, router.asPath, reset, query?.clear, clearErrors, isLoaded]);

  useEffect(() => {
    // Don't write to session storage if location is empty

    if (!formValues?.location) return;

    // Write form state to session storage
    localStorage.setItem(
      "travel_guide_form_values",
      JSON.stringify(formValues)
    );
  }, [formValues]);

  // useEffect(() => {
  //   // Errors are stored in session storage, like form values
  //   const sessionStorageErrors = sessionStorage.getItem(
  //     "travel_guide_form_errors"
  //   );

  //   if (!sessionStorageErrors) return;

  //   const sessionErrors = JSON.parse(sessionStorageErrors);

  //   if (!Object.keys(sessionErrors)?.length) {
  //     return;
  //   }

  //   // Clear session storage
  //   for (let error of Object.entries(sessionErrors)) {
  //     const [inputId, errorData] = error;
  //     setError(inputId, errorData);
  //   }
  // }, [router.asPath, setError]);

  const onSubmit = ({ responseData }) => {
    handleSubmit();
    setIsSubmitting(true);

    if (responseData) {
      setResponseData(responseData);
    }
  };

  function handlePageChange(pageNumber, doTrigger = true) {
    setCurrentPageNumber(pageNumber);

    if (!doTrigger) {
      return;
    }
    // Trigger validation on page change to ensure that Next button is disabled if page is not complete
    trigger();
  }

  const pageComponents = pages.map((page, index) => {
    return (
      <FormPage
        page={page}
        register={register}
        control={control}
        formValues={formValues}
        formState={formState}
        key={`${page}-${index}`}
        portalRef={portalRef}
      />
    );
  });

  const allPageComponents = [
    ...pageComponents,
    <GuideLoginPage key="guide-login-page" />,
  ];

  return (
    <div className={`${styles.container}`} ref={portalRef}>
      <div className={`${styles.imageWrapper}`}>
        <CldImage
          src={
            isSubmitting
              ? images[images.length - 1]
              : images[currentPageNumber - 1]
          }
          alt="Top-down view of a landscape divided by a highway."
          fill
          sizes={`
          (max-width: 768px) 600px,
          `}
        />
        <div className={`${styles.formContainer}`}>
          <div className={`${styles.form} `}>
            <FormProvider
              {...{
                register,
                control,
                watch,
                setValue,
                getValues,
                onSubmit,
                trigger,
                formState,
                setError,
                clearErrors,
                reset,
                generationStatus,
                setGenerationStatus,
                isSubmitting,
                setIsSubmitting,
              }}
            >
              {!isSubmitting && (
                <Pages onNav={handlePageChange}>{allPageComponents}</Pages>
              )}
              {isSubmitting && (
                <ConfirmationPage responseData={responseData} />
                // <ConfirmationPage
                //   responseData={{
                //     uuid: "836ead98-584a-4052-907e-1ede00c53493",
                //     enqueueResponse: {
                //       sucess: true,
                //     },
                //     enqueueDbResponse: {
                //       success: true,
                //       uuid: "836ead98-584a-4052-907e-1ede00c53493",
                //       status: "queued",
                //     },
                //   }}
                // />
              )}
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormPage({
  page,
  formValues,
  register,
  control,
  formState,
  portalRef,
}) {
  const pageInputs = page.inputs.map((input, index) => {
    const formProps = {
      register,
      control,
      formState,
    };

    const conditionalInputProps = {};

    if (input.controlled === true) {
      conditionalInputProps.currentValue = !!formValues[input.props.id]
        ? formValues[input.props.id]
        : null;
    }

    input.props = { ...input.props, ...conditionalInputProps, portalRef };

    return (
      <FormInput
        key={`${input.props.id}-${index}`}
        {...formProps}
        input={input}
      />
    );
  });

  return (
    <div className={`${styles.page}`}>
      <p className={`${styles.title}`}>{page.title}</p>
      <div className={`${styles.inputs}`}>{pageInputs}</div>
    </div>
  );
}
